import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@apollo/client'
import { useSubscriptionErrorReset } from 'hooks/useResetErrorSubscription'
import { Answer, Filter, Schema } from 'typings/filters'
import { GET_FILTER_VALUES, SOLITARY_SINGLE_QUERY } from 'query/query'
import { useAppliedFiltersAndContext } from 'hooks/useAppliedFiltersAndContext'
import { PREVIOUS_BRAND_VARIABLE, PREVIOUS_PURCHASED_BRAND } from 'hooks/useBrandSwitchSummary'
import { PageNameEnums } from 'hooks/useKBQDropdownOptions'
import { setSelectedDefaultPurchasedBrand } from 'reducers/KBQ/selectedBrand'
import { setKBQTopPurchasedData } from 'reducers/KBQ/kbqTopPurchasedData'
import { Data, DataPoint } from 'components/Charts/types'
import { KBQSelectOption } from 'components/KBQSelect'
import { PURCHASED_BRAND_ID } from 'kbqstories/pages/KBQPagesInfo'
import { getSelectedDefaultKBQBrands } from 'reducers/KBQ/selector'
import { RootState } from 'reducers/store'
import { CIE_ALLOWED_PATH } from 'util/urlUtils'
export interface FilterValuesResult {
  filterValues: Answer[]
}
export const PURCHASED_BRAND = 'purchasedBrand'
export const CONVERTED_SHOPPERS = 'converted shoppers'
export const NPSPROMOTERS = 'promoters'
export const NPSDETRACTORS = 'detractors'
export const LOYAL_SHOPPERS = 'loyal'
export const SWITCHERS = 'switchers'

export type PurchasedBrand = {
  label: string
  answer_id: number
  answer_code: number
}
interface PurchasedDataFromFilterValues {
  purchasedBrandsAsFilters?: FilterValuesResult
  sortedDataPoints?: DataPoint[]
}
interface UpdateData {
  brands?: Data
  loading: boolean
}
export const getTopPurchasedData = (data?: Data, topPurchasedData?: KBQSelectOption[]) => {
  if (data && topPurchasedData) {
    const topBrands = data.cross.series[0].dataPoints.slice(0, 6).map((dp) => dp.secondaryCode)
    const top5 =
      topBrands &&
      topPurchasedData
        ?.filter((item) => topBrands?.includes(item?.answer_id as number))
        .slice()
        .sort(
          (a, b) =>
            topBrands.indexOf(a?.answer_id as number) - topBrands.indexOf(b.answer_id as number)
        )
    return top5
  }
}

export const getTopPurchasedDataFromFilterValues = ({
  purchasedBrandsAsFilters,
  sortedDataPoints,
}: PurchasedDataFromFilterValues) => {
  return purchasedBrandsAsFilters?.filterValues.reduce<KBQSelectOption[]>(
    (acc, { label, answer_id, answer_code }) => {
      sortedDataPoints?.forEach((brandValues) => {
        if (
          Object.values(brandValues).includes(answer_id) &&
          !acc.some((item) => item.answer_id === answer_id)
        ) {
          acc.push({
            label,
            answer_id,
            answer_code,
          })
        }
      })

      return acc
    },
    []
  )
}
export const updateBrandsData = ({ brands, loading }: UpdateData) => {
  if (brands && !loading) {
    const data = Object.assign({}, brands, {
      cross: {
        ...brands.cross,
        series: brands.cross.series
          ? [brands.cross.summarySeries, ...brands.cross.series]
          : [brands.cross.summarySeries],
      },
    })
    return data
  }
  return null
}

export const typeOfStoriesPage = [
  PageNameEnums.BrandLostShoppers,
  PageNameEnums.BrandSwitchers,
  PageNameEnums.NpsWithShopperVoices,
]
const getPrimaryVarAndVariableId = (pathName?: string) => {
  let primaryVar
  let variableId
  if (
    (typeOfStoriesPage.includes(pathName!) && pathName === PageNameEnums.BrandSwitchers) ||
    pathName === CIE_ALLOWED_PATH.TABLE_OF_CONTENTS
  ) {
    primaryVar = PREVIOUS_PURCHASED_BRAND
    variableId = PREVIOUS_BRAND_VARIABLE
  } else if (
    typeOfStoriesPage.includes(pathName!) &&
    (pathName === PageNameEnums.BrandLostShoppers ||
      pathName === PageNameEnums.NpsWithShopperVoices)
  ) {
    primaryVar = PURCHASED_BRAND
    variableId = PURCHASED_BRAND_ID
  }
  return { primaryVar, variableId }
}
export const usePurchasedBrand = (localFilters?: Pick<Filter, 'variable_id' | 'answer_code'>[]) => {
  const pathName = window.location.pathname.split('/').pop()

  const { appliedFilters, context } = useAppliedFiltersAndContext()
  const { variableId, primaryVar } = getPrimaryVarAndVariableId(pathName)
  const {
    data: purchasedBrandsAsFilters,
    loading: filtersLoading,
    error: filtersError,
  } = useQuery<FilterValuesResult>(GET_FILTER_VALUES, {
    variables: {
      context,
      variableId,
      filters: appliedFilters,
      schema: Schema.CIE,
    },
  })
  const {
    data: brands,
    loading: brandsLoading,
    error: brandsError,
  } = useQuery<Data>(SOLITARY_SINGLE_QUERY, {
    variables: {
      primaryVar,
      filters: appliedFilters,
      removeDataPoints: [98, 95],
      context,
      localFilters,
      schema: Schema.CIE,
    },
  })
  const dispatch = useDispatch()

  const error = filtersError ?? brandsError
  const loading = filtersLoading || brandsLoading
  const updatedBrands = useMemo(() => updateBrandsData({ brands, loading }), [brands, loading])

  const topData = updatedBrands?.cross?.series[0]?.dataPoints[0]
  const sortedDataPoints = updatedBrands?.cross?.series[0].dataPoints
    .slice()
    .sort((a: any, b: any) => a.secondaryValue - b.secondaryValue)

  const topPurchasedData = !loading
    ? getTopPurchasedDataFromFilterValues({
        purchasedBrandsAsFilters: purchasedBrandsAsFilters,
        sortedDataPoints,
      })
    : undefined

  const topDataFromBrand = topPurchasedData?.filter(
    (value: any) => value.answer_id === topData?.secondaryCode
  )[0]
  const topBrandsData = getTopPurchasedData(brands, topPurchasedData)
  const defaultBrand = useSelector((state: RootState) =>
    getSelectedDefaultKBQBrands(state, { id: pathName! })
  )?.defaultBrand
  const setTopBrandsData = useCallback(
    (input) => {
      if (input && pathName) {
        dispatch(
          setSelectedDefaultPurchasedBrand({
            id: pathName?.trim(),
            defaultBrand: !defaultBrand ? input : defaultBrand,
          })
        )
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (updatedBrands && topDataFromBrand) {
      setTopBrandsData(topDataFromBrand)
      dispatch(
        setKBQTopPurchasedData({
          id: pathName!,
          topData: topBrandsData ?? [],
        })
      )
    }
  }, [updatedBrands, setTopBrandsData])

  useSubscriptionErrorReset(error)
  return {
    topPurchasedData,
    loading,
    error: !brands ? error : undefined,
    topBrandData: topDataFromBrand,
  }
}
